import React, { useState } from 'react';
import {
  HiArrowRight,
  HiArrowLeft,
  HiArrowPath,
  HiDeviceTablet,
  HiClock,
  HiSignal,
  HiWifi,
} from 'react-icons/hi2';
import { getDeviceDetails } from '../utils/api';

export default function SignalStatusStep({ ocrData, onBack, onNext, setOcrData }) {
  const [loading, setLoading] = useState(false);

  const lora = ocrData?.deviceInfo?.network?.loraWan;
  const rssi = lora?.rssi ?? null;
  const snr = lora?.snr ?? null;
  const spFact = lora?.spFact ?? null;
  const lrrCnt = lora?.lrrCnt ?? null;
  const lrrId = lora?.lrrId ?? null;
  const lastOnline = ocrData?.deviceInfo?.lastOnline;
  const channels = ocrData?.deviceInfo?.channelList || [];
  const deviceId = ocrData?.eui;

  const lastUpdated = new Date().toLocaleString();

  const getColor = (value, type) => {
    if (type === 'rssi') return value >= -90 ? 'text-green-600' : value >= -110 ? 'text-yellow-500' : 'text-red-500';
    if (type === 'snr') return value >= 5 ? 'text-green-600' : value >= 0 ? 'text-yellow-500' : 'text-red-500';
    if (type === 'spfact') return value <= 7 ? 'text-green-600' : value <= 10 ? 'text-yellow-500' : 'text-red-500';
    return 'text-gray-600';
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const updatedInfo = await getDeviceDetails(deviceId);
      setOcrData((prev) => ({
        ...prev,
        deviceInfo: updatedInfo,
      }));
    } catch (err) {
      console.error('Failed to refresh device data:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col justify-between flex-grow space-y-6 px-4 sm:px-6 md:px-8">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
          <h1 className="text-base sm:text-2xl font-semibold text-gray-800 break-words text-center sm:text-left">
            Device: <span className="font-bold break-words">{deviceId}</span>
          </h1>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded hover:bg-primary/90 text-sm sm:text-base sm:ml-auto disabled:opacity-70"
            title="Refresh"
          >
            <HiArrowPath className={`w-4 h-4 sm:w-5 sm:h-5 ${loading ? 'animate-spin' : ''}`} />
            <span className="block sm:hidden">Refresh</span>
          </button>
        </div>

        {/* General Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border rounded-lg shadow-sm p-4 text-center">
            <p className="text-sm text-gray-600 flex items-center justify-center gap-1">
              <HiDeviceTablet className="w-4 h-4 shrink-0" /> TYPE
            </p>
            <p className="text-lg font-bold text-gray-800 break-words">{ocrData?.deviceType || ocrData?.deviceInfo?.deviceType || '—'}</p>
          </div>
          <div className="border rounded-lg shadow-sm p-4 text-center">
            <p className="text-sm text-gray-600 flex items-center justify-center gap-1">
              <HiClock className="w-4 h-4 shrink-0" /> LAST ONLINE
            </p>
            <p className="text-base font-semibold text-gray-800">
              {lastOnline ? new Date(lastOnline).toLocaleString() : '—'}
            </p>
            <p className="text-xs italic text-gray-500">
              {lastOnline ? new Date(lastOnline).toLocaleDateString(undefined, { dateStyle: 'long' }) : ''}
            </p>
          </div>
        </div>

        <p className="text-xs italic text-right text-gray-500 mt-1">last updated {lastUpdated}</p>

        {/* Network Section */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-gray-800">LoRaWAN Network</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-center">
            {[{ label: 'SPFact', value: spFact, icon: HiSignal, type: 'spfact' },
              { label: 'SNR', value: snr, icon: HiWifi, type: 'snr' },
              { label: 'RSSI', value: rssi, icon: HiWifi, type: 'rssi' }].map(({ label, value, icon: Icon, type }, idx) => (
              <div key={idx} className="border rounded-lg shadow-sm p-4 flex justify-evenly items-center">
                <div className="flex flex-col">
                  <p className="text-sm text-gray-600">{label}</p>
                  <p className="text-lg font-semibold text-gray-900">{value ?? '—'}</p>
                </div>
                <Icon className={`w-8 h-8 ${getColor(value, type)}`} />
              </div>
            ))}
            <div className="border rounded-lg shadow-sm p-4 text-center">
              <p className="text-sm text-gray-600">LRR Count</p>
              <p className="text-base font-bold text-gray-800">{lrrCnt ?? '—'}</p>
            </div>
            <div className="border rounded-lg shadow-sm p-4 text-center">
              <p className="text-sm text-gray-600">Last LRRID</p>
              <p className="text-base font-bold text-gray-800 break-words">{lrrId ?? '—'}</p>
            </div>
          </div>
        </div>

        {/* Channel Readings */}
        {channels.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Readings</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-4 py-2 border">Ch.</th>
                    <th className="px-4 py-2 border">Type</th>
                    <th className="px-4 py-2 border">Value</th>
                    <th className="px-4 py-2 border">Label</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((ch, idx) => (
                    <tr key={idx} className="odd:bg-white even:bg-gray-50">
                      <td className="px-4 py-2 border text-center">{ch.channelId}</td>
                      <td className="px-4 py-2 border break-words">{ch.type}</td>
                      <td className="px-4 py-2 border">{ch.value} {ch.unit || ''}</td>
                      <td className="px-4 py-2 border break-words">{ch.label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="pt-6 flex justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-gray-200 text-sm text-gray-800 hover:bg-gray-300 transition"
        >
          <HiArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onNext}
          className="inline-flex items-center justify-center gap-2 px-6 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition"
        >
          Next
          <HiArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
